import React, { useMemo, useState } from 'react'
import LoadingSpinner from "../../components/LoadingSpinner.js";
import { DateTimeSelector, OffsetSelector } from '../components';
import { Pagination } from '@CommonComponents/buttons';
import { PAGINATION_RATE } from '../constants';
import { useStateValue } from '@State';
import { actions } from '../reducer';

export function StudentViewer({ allStudents, state }) {
  const components = {
    'loading': {
      Component: LoadingSpinner,
      props: {}
    },
    'loaded': {
      Component: StudentsTable,
      props: { allStudents }
    }
  }

  const { Component, props } = state ? components[state] : components['loading']
  return <div className="card-myed">
    <h3>Students</h3>
    <Component {...props}></Component>
  </div>
}

function compareStudents(a, b) {
  return (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' })
    || (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' })
    || a.email.localeCompare(b.email)
}

function studentSearchText(student) {
  return `${student.first_name} ${student.last_name} ${student.last_name}, ${student.first_name} ${student.email}`.toLowerCase()
}

function StudentsTable({ allStudents }) {
  const [{ strategy, selectedStudents }, dispatch] = useStateValue()
  const inputType = strategy.studentInputType
  const disabled = strategy.disableInputs
  const [currentPage, setCurrentPage] = useState(1)
  const [filterText, setFilterText] = useState('')

  const sortedStudents = useMemo(
    () => Object.values(allStudents).sort(compareStudents),
    [allStudents]
  )
  const needle = filterText.trim().toLowerCase()
  const filteredStudents = useMemo(
    () => needle
      ? sortedStudents.filter(student => studentSearchText(student).includes(needle))
      : sortedStudents,
    [sortedStudents, needle]
  )
  const startIdx = (currentPage - 1) * PAGINATION_RATE
  const paginatedStudents = filteredStudents.slice(startIdx, startIdx + PAGINATION_RATE)

  // Select-all operates on the filtered set, so a filter can be used to bulk-select a subset
  const allFilteredSelected = filteredStudents.length > 0 && filteredStudents.every(student => selectedStudents.has(student.email))

  function handleFilterChange(e) {
    setFilterText(e.target.value)
    setCurrentPage(1)
  }

  function handleSelectAllCheckbox() {
    dispatch({
      type: allFilteredSelected ? actions.UNSELECT_STUDENTS : actions.SELECT_STUDENTS,
      emails: filteredStudents.map(student => student.email),
    })
  }

  return <div >
    <div className="card-myed">
      <div className="form-group">
        <input
          type="text"
          className="form-control"
          placeholder="Filter by name or email"
          aria-label="Filter students by name or email"
          value={filterText}
          onChange={handleFilterChange}
        />
      </div>
      <table className='table table-striped table-hover'>
        <thead>
          <tr key={'students'} onClick={() => { if (!disabled) handleSelectAllCheckbox() }}>
            <th scope="col" style={{ width: '25px' }}>
              <input disabled={disabled} type='checkbox' checked={allFilteredSelected} readOnly></input>
            </th>
            <th scope="col" style={{ width: '250px' }}>Email</th>
            <th scope="col">Name</th>
          </tr>
        </thead>
        <tbody>
          {paginatedStudents.map(student => {
            return <tr key={`student-viewer-${student.email}`} onClick={() => { if (!disabled) dispatch({ type: actions.TOGGLE_STUDENT, email: student.email }) }}>
              <td>
                <input disabled={disabled} readOnly type='checkbox' checked={selectedStudents.has(student.email)}></input>
              </td>
              <td>{student.email}</td>
              <td>{`${student.last_name}, ${student.first_name}`}</td>
            </tr>
          })}
          {filteredStudents.length === 0 &&
            <tr>
              <td colSpan="3" className="text-muted">No students match the filter.</td>
            </tr>
          }
        </tbody>
      </table>
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        rate={PAGINATION_RATE}
        total={filteredStudents.length}
      />
    </div>
    {/* Date/offset inputs sit below the table so they're next to the Pending Changes
        panel — adjusting an offset after reviewing the preview shouldn't require
        scrolling back up past the student list. */}
    {inputType && inputType === 'date' &&
      <div className="card-myed">
        <DateTimeSelector
          disabled={disabled}
          onChange={(date) => dispatch({ type: actions.UPDATE_DATE, date })}
        ></DateTimeSelector>
      </div>
    }
    {inputType && inputType === 'offset' &&
      <div className="card-myed">
        <OffsetSelector
          disabled={disabled}
          onChange={(key, value) => dispatch({ type: actions.UPDATE_OFFSET, key, value })}
        ></OffsetSelector>
      </div>
    }
  </div>
}
